"use client";

import Link from "next/link";
import axios from "axios";
import { useAuthStore } from "@/store/auth";
import type { CommunityPostOut } from "@/types";

// 마이그 101 (2026-05-25) — 글 종류별 색상 (네이버 카페 스타일)
const TOPIC_COLORS: Record<string, string> = {
  poll: "bg-yellow-400/10 text-yellow-400 border-yellow-400/30",
  item_tagged: "bg-primary/10 text-primary border-primary/30",
  free: "bg-bg-elevated text-text-sub border-border",
};
const CONTENT_CAT_COLORS: Record<string, string> = {
  insight: "bg-emerald-500/10 text-emerald-500 border-emerald-500/30",
  recipe: "bg-blue-500/10 text-blue-500 border-blue-500/30",
  operation: "bg-red-500/10 text-red-500 border-red-500/30",
  new_menu: "bg-purple-500/10 text-purple-500 border-purple-500/30",
  casual: "bg-gray-500/10 text-gray-400 border-gray-500/30",
  question: "bg-orange-500/10 text-orange-500 border-orange-500/30",
};
const CONTENT_CAT_LABEL: Record<string, string> = {
  insight: "매출후기",
  recipe: "레시피",
  operation: "운영고민",
  new_menu: "신메뉴",
  casual: "잡담",
  question: "질문",
};
// 마이그 105 — 중고장터 라벨/색상
const MARKET_STATUS_LABEL: Record<string, string> = {
  selling: "판매중",
  reserved: "예약중",
  sold: "거래완료",
};

/** 거래단계 라벨 — 삽니다(buy)는 'selling'을 '구매중'으로 표기 (판매중은 팝니다 전용). */
export function marketStatusLabel(status?: string | null, tradeType?: string | null): string {
  if (!status) return "";
  if (status === "selling") return tradeType === "buy" ? "구매중" : "판매중";
  return MARKET_STATUS_LABEL[status] ?? status;
}
const MARKET_STATUS_COLOR: Record<string, string> = {
  selling: "bg-emerald-500/10 text-emerald-500 border-emerald-500/30",
  reserved: "bg-amber-500/10 text-amber-500 border-amber-500/30",
  sold: "bg-gray-500/10 text-gray-400 border-gray-500/30",
};
const TRADE_METHOD_LABEL: Record<string, string> = {
  direct: "직거래",
  delivery: "택배",
  both: "직거래+택배",
};

function fmtMarketPrice(post: CommunityPostOut): string {
  if (post.price_type === "free") return "무료나눔";
  if (post.price_type === "negotiable") return "가격협의";
  if (post.price != null) return `${post.price.toLocaleString()}원`;
  return "가격협의";
}

/** 조회수 컴팩트 표기 — 999 → '999', 1.2k → '1.2k', 1만 → '1만' */
export function fmtCount(n: number): string {
  if (n < 1000) return String(n);
  if (n < 10000) {
    const k = n / 1000;
    return k >= 10 ? `${Math.floor(k)}k` : `${k.toFixed(1)}k`;
  }
  const man = n / 10000;
  return man >= 10 ? `${Math.floor(man)}만` : `${man.toFixed(1)}만`;
}

/** 컴팩트 시간 (1줄 표기용) — '방금', '5분', '3시간', '5/25', '26.5.3' */
export function compactTime(d: Date): string {
  const now = new Date();
  const diff = (now.getTime() - d.getTime()) / 1000;
  if (diff < 60) return "방금";
  if (diff < 3600) return `${Math.floor(diff / 60)}분`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}시간`;
  if (d.getFullYear() === now.getFullYear()) {
    return `${d.getMonth() + 1}/${d.getDate()}`;
  }
  return `${String(d.getFullYear()).slice(2)}.${d.getMonth() + 1}.${d.getDate()}`;
}

/**
 * 네이버 카페 스타일 한 줄 row (마이그 101, 2026-05-25). 커뮤니티 목록 + 내 게시글 공용.
 *
 * 행 구조 (모바일 우선):
 *   [색칩 카테고리]  제목 (댓글수)                  ⭐
 *   @닉네임 · 날짜 · 👁N
 *   (장터인 경우 가격·상태·카테고리 1줄)
 *   (찬반인 경우 progress bar 1줄)
 */
export function PostRow({
  post, read, onClick, onBookmarkToggle,
}: {
  post: CommunityPostOut;
  read: boolean;
  onClick: () => void;
  onBookmarkToggle: (newState: boolean) => void;
}) {
  const { token } = useAuthStore();
  const rel = compactTime(new Date(post.created_at));

  // 카테고리 색칩 — 우선순위: market > content_category > post_type
  let chipLabel = "자유";
  let chipClass = TOPIC_COLORS.free;
  if (post.post_type === "market") {
    chipLabel = post.trade_type === "buy" ? "삽니다" : "팝니다";
    chipClass = post.trade_type === "buy"
      ? "bg-blue-500/10 text-blue-500 border-blue-500/30"
      : "bg-rose-500/10 text-rose-500 border-rose-500/30";
  } else if (post.content_category && CONTENT_CAT_COLORS[post.content_category]) {
    chipLabel = CONTENT_CAT_LABEL[post.content_category] ?? post.content_category;
    chipClass = CONTENT_CAT_COLORS[post.content_category];
  } else if (post.post_type === "poll") {
    chipLabel = "찬반";
    chipClass = TOPIC_COLORS.poll;
  } else if (post.post_type === "item_tagged") {
    chipLabel = "태그";
    chipClass = TOPIC_COLORS.item_tagged;
  }
  // 운영자 글은 [공지] 로 override (시각적 신뢰성 ↑)
  if (post.author_is_admin) {
    chipLabel = "공지";
    chipClass = "bg-red-500/10 text-red-400 border-red-500/30";
  }

  const handleBookmark = async (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (!token) { alert("로그인이 필요합니다."); return; }
    try {
      const r = await axios.post<{ bookmarked: boolean }>(
        `/api/community/posts/${post.id}/bookmark`,
        {},
        { headers: { Authorization: `Bearer ${token}` } },
      );
      onBookmarkToggle(r.data.bookmarked);
    } catch (e) { alert("실패: " + String(e)); }
  };

  // 제목: free/poll/market 은 title, 그 외는 content 첫 줄
  const titleText = (post.post_type === "free" || post.post_type === "poll" || post.post_type === "market") && post.title
    ? post.title
    : (post.content || "").split("\n")[0].slice(0, 80) || "(제목 없음)";

  return (
    <Link
      href={`/community/${post.id}`}
      onClick={onClick}
      className={`block border-b border-border/40 hover:bg-bg-elevated/40 transition-colors ${read ? "opacity-60" : ""} ${post.author_is_admin ? "bg-red-500/5" : ""}`}
    >
      <div className="px-3 py-2.5">
        {/* 1번째 줄: 분류 색칩 + 제목 (전폭) + ★ — 제목 글자수 확보 (2026-05-26) */}
        <div className="flex items-start gap-2">
          {/* 분류 색칩 */}
          <span className={`text-2xs font-bold px-1.5 py-0.5 rounded border shrink-0 text-center w-[48px] ${chipClass}`}>
            {chipLabel}
          </span>
          {/* 제목 — 두 줄까지 노출 */}
          <p className={`flex-1 min-w-0 text-sm font-bold leading-snug line-clamp-2 ${read ? "text-text-sub" : "text-text"}`}>
            {titleText}
            {post.comment_count > 0 && (
              <span className="ml-1 text-primary font-bold text-2xs">[{post.comment_count}]</span>
            )}
            {post.images && post.images.length > 0 && (
              <span className="ml-1 text-text-sub text-2xs">📷</span>
            )}
            {post.insight && (
              <span className="ml-1 text-emerald-500 text-2xs">📊</span>
            )}
          </p>
          {/* 북마크 */}
          <button
            onClick={handleBookmark}
            className={`w-[16px] text-center text-base shrink-0 leading-none mt-0.5 ${post.bookmarked ? "text-yellow-400" : "text-text-sub/30 hover:text-yellow-400"}`}
            aria-label={post.bookmarked ? "북마크 해제" : "북마크"}
          >
            {post.bookmarked ? "★" : "☆"}
          </button>
        </div>

        {/* 2번째 줄: 메타 (닉 · 시간 · 조회) — 분류 chip 폭만큼 들여쓰기 */}
        <div className="flex items-center gap-1.5 mt-1 ml-[56px] text-2xs text-text-sub">
          <span className="truncate max-w-[40%]">@{post.author_name || "익명"}</span>
          <span className="text-text-sub/40">·</span>
          <span className="tabular-nums shrink-0">{rel}</span>
          <span className="text-text-sub/40">·</span>
          <span className="tabular-nums shrink-0">조회 {fmtCount(post.view_count ?? 0)}</span>
        </div>

        {/* 장터 — 가격 · 상태 · 카테고리 (market 일 때만) */}
        {post.post_type === "market" && (
          <div className="mt-1 ml-[56px] flex flex-wrap items-center gap-1.5 text-2xs">
            <span className={`text-xs font-black ${post.market_status === "sold" ? "text-text-sub line-through" : "text-text"}`}>
              {fmtMarketPrice(post)}
            </span>
            {post.market_status && (
              <span className={`font-bold px-1.5 py-0.5 rounded border ${MARKET_STATUS_COLOR[post.market_status]}`}>
                {marketStatusLabel(post.market_status, post.trade_type)}
              </span>
            )}
            {post.market_category && (
              <span className="text-text-sub">· {post.market_category}</span>
            )}
            {post.trade_method && (
              <span className="text-text-sub">· {TRADE_METHOD_LABEL[post.trade_method]}</span>
            )}
          </div>
        )}

        {/* 찬반 — progress bar (poll 일 때만) */}
        {post.poll && post.poll.total > 0 && (
          <div className="mt-1.5 ml-[56px] flex items-center gap-2 text-2xs">
            <div className={`flex-1 bg-bg-elevated rounded-full h-1 overflow-hidden flex ${post.poll.is_closed ? "opacity-60" : ""}`}>
              <div className="bg-primary" style={{ width: `${Math.round((post.poll.pro_count / post.poll.total) * 100)}%` }} />
              <div className="bg-red-400" style={{ width: `${Math.round((post.poll.con_count / post.poll.total) * 100)}%` }} />
            </div>
            <span className="text-text-sub shrink-0 tabular-nums">
              {Math.round((post.poll.pro_count / post.poll.total) * 100)}% · {post.poll.total}명{post.poll.is_closed && " · 종료"}
            </span>
          </div>
        )}
      </div>
    </Link>
  );
}
